import {
  type ActionMenuItem,
  AlertDialog,
  Collapsible,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import type { RequestGroup, ServiceRequest } from "@curai/js-fhir-tools";
import { TestTubeDiagonal, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useCarePlanSummary } from "../network/useCarePlanSummary";
import { OrderEntry } from "./OrderEntry/OrderEntry";
import { SubmittedOrder } from "./OrderEntry/SubmittedOrder";
import { useCancelOrder } from "./network/useCancelOrder";
import type {
  LabOrderEntryActionSummary,
  LabOrderEntryStatusSummary,
} from "./network/useLabOrdersByEncounter";
import { useLabOrdersByEncounter } from "./network/useLabOrdersByEncounter";
import { useLabOrderUpdateStatus } from "./network/useUpdateLabOrder";

interface LabOrderProps {
  index: number;
  requestGroup: RequestGroup;
  serviceRequests: ServiceRequest[];
  statusSummary: LabOrderEntryStatusSummary;
  actionSummary: LabOrderEntryActionSummary;
  encounterId: string;
  carePlanId: string;
  patientId: string;
  readOnly?: boolean;
}

export const LabOrderV2 = ({
  index,
  encounterId,
  patientId,
  requestGroup,
  serviceRequests,
  statusSummary,
  actionSummary,
  readOnly = false,
}: LabOrderProps) => {
  const [open, setOpen] = useState(!statusSummary.submittedToLab);
  const [showRefundPrompt, setShowRefundPrompt] = useState(false);
  const [showAreYouReallySurePrompt, setShowAreYouReallySurePrompt] =
    useState(false);

  const { isFetching: isFetchingLabOrdersByEncounter } =
    useLabOrdersByEncounter({
      patientId,
      encounterId,
      enabled: true,
    });

  const { isFetching: isFetchingCareplanSummary } =
    useCarePlanSummary(encounterId);

  const cancelLabOrder = useCancelOrder({
    patientId,
    encounterId,
    requestGroupId: requestGroup.id as string,
  });

  const { isUpdatingLabOrder } = useLabOrderUpdateStatus({
    patientId,
    encounterId,
    requestGroupId: requestGroup.id as string,
  });

  const isAnythingBlocking =
    isFetchingLabOrdersByEncounter ||
    isFetchingCareplanSummary ||
    cancelLabOrder.isPending ||
    isUpdatingLabOrder;

  const handleCancelLabOrder = useCallback(async () => {
    trackEvent(TRACKERS.CANCEL_LAB_ORDER);
    try {
      await cancelLabOrder.mutateAsync();
    } catch {
      toast.error("An error occurred cancelling the order.");
    }
  }, [cancelLabOrder]);

  const actionMenu = useMemo(() => {
    const menuItems: ActionMenuItem[] = [];
    if (actionSummary.canCancel) {
      menuItems.push({
        label: "Cancel Order",
        onClick: handleCancelLabOrder,
        icon: <Trash2 size={16} />,
        variant: "danger",
      });
    } else if (actionSummary.canCancelWithoutPaymentCheck) {
      menuItems.push({
        label: "Cancel Order",
        onClick: () => {
          setShowRefundPrompt(true);
        },
        icon: <Trash2 size={16} />,
        variant: "danger",
      });
    } else {
      // Force cancel because the clinicians keep asking for it... 🤷🏻‍♀️
      menuItems.push({
        label: "Cancel Order",
        onClick: () => {
          setShowAreYouReallySurePrompt(true);
        },
        icon: <Trash2 size={16} />,
        variant: "danger",
      });
    }
    return menuItems;
  }, [
    actionSummary.canCancel,
    actionSummary.canCancelWithoutPaymentCheck,
    handleCancelLabOrder,
  ]);

  return (
    <>
      <Collapsible
        title={`Lab Order ${index + 1}`}
        titleIcon={<TestTubeDiagonal className="w-4 h-4" />}
        headerSize="sm"
        background="secondary"
        status={
          serviceRequests.length
            ? `${serviceRequests.length.toString()} Test${serviceRequests.length > 1 ? "s" : ""}`
            : "No Tests"
        }
        forcedDropdownActions={actionMenu}
        dropdownActionsLoading={isAnythingBlocking}
        onOpenChange={(open) => {
          trackEvent(open ? TRACKERS.EXPAND_LABS : TRACKERS.COLLAPSE_LABS);
          setOpen(open);
        }}
        open={open}
        controlled={true}
        testId="lab-order-v2"
      >
        <div className="grid gap-2">
          {statusSummary.submittedToLab ? (
            <SubmittedOrder
              encounterId={encounterId}
              requestGroup={requestGroup}
              serviceRequests={serviceRequests}
              actionSummary={actionSummary}
            />
          ) : (
            <OrderEntry
              readOnly={readOnly}
              patientId={patientId}
              encounterId={encounterId}
              requestGroup={requestGroup}
              serviceRequests={serviceRequests}
              actionSummary={actionSummary}
              statusSummary={statusSummary}
            />
          )}
        </div>
      </Collapsible>
      <AlertDialog
        isOpen={showRefundPrompt}
        onOpenChange={setShowRefundPrompt}
        isControlled
        title="Warning: Pre-Paid Order"
        body="This order has been paid for by the patient and will not be automatically refunded. Before canceling, contact support at support@curai.com to manually initiate a refund process for this patient."
        actionLabel="Cancel Order"
        cancelLabel="Go Back"
        onAction={() => {
          handleCancelLabOrder();
          setShowRefundPrompt(false);
        }}
        onCancel={() => {
          setShowRefundPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
      <AlertDialog
        isOpen={showAreYouReallySurePrompt}
        onOpenChange={setShowAreYouReallySurePrompt}
        isControlled
        title="Warning: Order Already Submitted"
        body="This order has already been submitted to the lab. Cancelling this order will remove it from the patient's chart, but will not inform the lab of the cancellation."
        actionLabel="Cancel Order"
        cancelLabel="Go Back"
        onAction={() => {
          handleCancelLabOrder();
          setShowAreYouReallySurePrompt(false);
        }}
        onCancel={() => {
          setShowAreYouReallySurePrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
